import { useCallback, useEffect, useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import styled from "@emotion/styled";
import { Variant } from "@leafygreen-ui/badge";
import { Tab } from "@leafygreen-ui/tabs";
import { Body } from "@leafygreen-ui/typography";
import { useNavigate, useParams } from "react-router-dom";
import { StyledLink } from "@evg-ui/lib/components/styles";
import { size } from "@evg-ui/lib/constants/tokens";
import { useQueryParams } from "@evg-ui/lib/hooks";
import { useTaskAnalytics } from "analytics";
import { TrendChartsPlugin } from "components/PerfPlugin";
import { StyledTabs } from "components/styles/StyledTabs";
import { TabLabelWithBadge } from "components/TabLabelWithBadge";
import { getHoneycombHistoryUrl } from "constants/externalResources/honeycomb";
import { GetTaskRouteOptions, getTaskRoute, slugs } from "constants/routes";
import {
  TaskPerfPluginEnabledQuery,
  TaskPerfPluginEnabledQueryVariables,
  TaskQuery,
  TaskTestCountQuery,
  TaskTestCountQueryVariables,
} from "gql/generated/types";
import { TASK_PERF_PLUGIN_ENABLED, TASK_TEST_COUNT } from "gql/queries";
import { useProjectBuildBaronSettings } from "hooks";
import { useTabShortcut } from "hooks/useTabShortcut";
import { TaskTab } from "types/task";
import { statuses } from "utils";
import BuildBaron from "./buildBaronAndAnnotations";
import ExecutionTasksTable from "./ExecutionTasksTable";
import ExecutionTasksTiming from "./ExecutionTasksTiming";
import FileTable from "./FileTable";
import Logs from "./logs";
import { TaskConfigTab } from "./TaskConfig";
import TaskHistory from "./TaskHistory";
import { walkthroughHistoryTabProps } from "./TaskHistory/constants";
import TestsTable from "./testsTable/TestsTable";
import { getDefaultTab } from "./utils/getDefaultTab";

const { isFailedTaskStatus } = statuses;

interface TaskTabProps {
  isDisplayTask: boolean;
  task: NonNullable<TaskQuery["task"]>;
}

const useTabConfig = (
  task: NonNullable<TaskQuery["task"]>,
  taskTestCountData: TaskTestCountQuery["task"],
  taskPerfPluginEnabledData: TaskPerfPluginEnabledQuery["task"],
  isDisplayTask: boolean,
) => {
  const { failedTestCount } = taskTestCountData || {};
  const { isPerfPluginEnabled } = taskPerfPluginEnabledData || {};
  const {
    annotation,
    baseTask,
    buildVariant,
    canModifyAnnotation,
    displayName,
    displayStatus,
    execution,
    executionTasksFull,
    files,
    id,
    logs: logLinks,
    project,
    requester,
    versionMetadata,
  } = task;
  const baseTaskId = baseTask?.id || "";
  const { fileCount } = files ?? {};
  const { id: projectId, identifier: projectIdentifier } = project || {};
  const isFailedTask = isFailedTaskStatus(displayStatus);
  const { bbTicketCreationDefined, buildBaronConfigured } =
    useProjectBuildBaronSettings({
      projectId,
      projectIdentifier,
      shouldFetch: isFailedTask,
    });
  const showBuildBaron =
    isFailedTask &&
    (buildBaronConfigured || !!annotation || canModifyAnnotation);

  const tabIsActive: Record<TaskTab, boolean> = {
    // Display tasks have no execution logs, but the Logs tab still surfaces
    // their event log (e.g. created Jira tickets, restarts).
    [TaskTab.Logs]: true,
    [TaskTab.ExecutionTasks]: isDisplayTask,
    [TaskTab.Tests]: true,
    [TaskTab.Files]: true,
    [TaskTab.Annotations]: showBuildBaron,
    [TaskTab.TrendCharts]: !!isPerfPluginEnabled,
    [TaskTab.History]: true,
    [TaskTab.ExecutionTasksTiming]:
      isDisplayTask && !!executionTasksFull && executionTasksFull.length > 0,
    [TaskTab.Config]: !isDisplayTask,
  };

  const tabMap: Record<TaskTab, React.JSX.Element> = {
    [TaskTab.Logs]: (
      <Tab key="task-logs-tab" data-testid="task-logs-tab" name="Logs">
        <Logs
          execution={execution}
          isDisplayTask={isDisplayTask}
          logLinks={logLinks}
          taskId={id}
        />
      </Tab>
    ),
    [TaskTab.Tests]: (
      <Tab
        key="task-tests-tab"
        data-testid="task-tests-tab"
        name={
          failedTestCount ? (
            <TabLabelWithBadge
              badgeText={failedTestCount}
              badgeVariant={Variant.Red}
              dataTestIdBadge="tests-tab-badge"
              tabLabel="Tests"
            />
          ) : (
            "Tests"
          )
        }
      >
        <TestsTable task={task} />
      </Tab>
    ),
    [TaskTab.ExecutionTasks]: (
      <Tab
        key="execution-tasks-tab"
        data-testid="task-execution-tab"
        name="Execution Tasks"
      >
        <ExecutionTasksTable
          execution={execution}
          executionTasksFull={executionTasksFull}
          isPatch={versionMetadata?.isPatch}
        />
      </Tab>
    ),
    [TaskTab.Files]: (
      <Tab
        key="task-files-tab"
        data-testid="task-files-tab"
        name={
          fileCount !== undefined ? (
            <TabLabelWithBadge
              badgeText={fileCount}
              badgeVariant={Variant.LightGray}
              dataTestIdBadge="files-tab-badge"
              tabLabel="Files"
            />
          ) : (
            "Files"
          )
        }
      >
        <FileTable execution={execution} taskId={id} />
      </Tab>
    ),
    [TaskTab.Annotations]: (
      <Tab
        key="task-build-baron-tab"
        data-testid="task-build-baron-tab"
        name="Failure Details"
      >
        <BuildBaron
          /* @ts-expect-error: FIXME. This comment was added by an automated script. */
          annotation={annotation}
          bbTicketCreationDefined={bbTicketCreationDefined}
          buildBaronConfigured={buildBaronConfigured}
          execution={execution}
          taskId={id}
          userCanModify={canModifyAnnotation}
        />
      </Tab>
    ),
    [TaskTab.TrendCharts]: (
      <Tab
        key="trend-charts-tab"
        data-testid="trend-charts-tab"
        name="Trend Charts"
      >
        <TrendChartsPlugin taskId={id} />
      </Tab>
    ),
    [TaskTab.History]: (
      <Tab
        key="task-history-tab"
        data-testid="task-history-tab"
        name="History"
        {...walkthroughHistoryTabProps}
      >
        {baseTaskId ? (
          <TaskHistory baseTaskId={baseTaskId} task={task} />
        ) : (
          <TaskHistoryDisclaimer>
            <Body>
              Evergreen cannot show history for this task; try viewing the{" "}
              <StyledLink
                href={getHoneycombHistoryUrl({
                  bvName: buildVariant,
                  projectId: projectId ?? "",
                  taskName: displayName,
                  requester,
                  isDisplayTask,
                })}
              >
                history in Honeycomb
              </StyledLink>{" "}
              instead.
            </Body>
            <Body>
              (Note that if this is a merge queue task, history may become
              available upon task completion.)
            </Body>
          </TaskHistoryDisclaimer>
        )}
      </Tab>
    ),
    [TaskTab.ExecutionTasksTiming]: (
      <Tab
        key="execution-tasks-timing-tab"
        data-testid="execution-tasks-timing-tab"
        name="Execution Tasks Timing"
      >
        <ExecutionTasksTiming
          executionTasksFull={executionTasksFull}
          taskName={displayName}
        />
      </Tab>
    ),
    [TaskTab.Config]: (
      <Tab
        key="task-config-tab"
        data-testid="task-config-tab"
        name={
          <TabLabelWithBadge
            badgeText="New"
            badgeVariant={Variant.Blue}
            tabLabel="Task Config"
          />
        }
      >
        <TaskConfigTab execution={execution} taskId={id} />
      </Tab>
    ),
  };

  const activeTabs = Object.keys(tabIsActive).filter(
    (tab) => tabIsActive[tab as TaskTab],
  ) as TaskTab[];

  return { tabMap, activeTabs };
};

const TaskHistoryDisclaimer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${size.xs};
`;

const TaskTabs: React.FC<TaskTabProps> = ({ isDisplayTask, task }) => {
  const { [slugs.tab]: urlTab } = useParams<{ [slugs.tab]: TaskTab }>();
  const taskAnalytics = useTaskAnalytics();
  const { data: taskTestCountData } = useQuery<
    TaskTestCountQuery,
    TaskTestCountQueryVariables
  >(TASK_TEST_COUNT, {
    variables: {
      taskId: task.id,
      execution: task.execution,
    },
  });
  const { data: taskPerfPluginEnabledData } = useQuery<
    TaskPerfPluginEnabledQuery,
    TaskPerfPluginEnabledQueryVariables
  >(TASK_PERF_PLUGIN_ENABLED, {
    variables: {
      taskId: task.id,
      execution: task.execution,
    },
  });
  const { failedTestCount } = taskTestCountData?.task || {};

  const [params] = useQueryParams();
  const navigate = useNavigate();
  const { activeTabs, tabMap } = useTabConfig(
    task,
    taskTestCountData?.task,
    taskPerfPluginEnabledData?.task,
    isDisplayTask,
  );

  const tabIndex = useMemo(
    () =>
      getDefaultTab({
        activeTabs,
        failedTestCount: failedTestCount ?? 0,
        isDisplayTask,
        urlTab,
      }),
    [activeTabs, failedTestCount, isDisplayTask, urlTab],
  );

  // Update the default tab in the url if it isn't populated or if the tab is not the same as the current tab
  useEffect(() => {
    const isValidTab = urlTab && activeTabs.includes(urlTab);
    if (urlTab === undefined || !isValidTab) {
      navigate(
        getTaskRoute(task.id, {
          ...params,
          tab: activeTabs[tabIndex],
        } as GetTaskRouteOptions),
        { replace: true },
      );
      if (urlTab === undefined) {
        taskAnalytics.sendEvent({
          name: "Redirected to default tab",
          tab: activeTabs[tabIndex],
        });
      }
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlTab, activeTabs, params, task.id, task.execution, tabIndex]);

  const setURLTab = useCallback(
    (newTabIndex: number) => {
      const newUrl = getTaskRoute(task.id, {
        ...params,
        execution: task.execution,
        tab: activeTabs[newTabIndex],
      } as GetTaskRouteOptions);
      navigate(newUrl, { replace: true });
    },
    [task.id, task.execution, params, activeTabs, navigate],
  );

  const handleTabChange = (newTab: React.SetStateAction<number>) => {
    const newTabIndex =
      typeof newTab === "function"
        ? newTab(activeTabs.indexOf(urlTab ?? activeTabs[tabIndex]))
        : newTab;
    taskAnalytics.sendEvent({
      name: "Changed tab",
      tab: activeTabs[newTabIndex],
    });
    setURLTab(newTabIndex);
  };
  useTabShortcut({
    currentTab: tabIndex,
    numTabs: activeTabs.length,
    setSelectedTab: handleTabChange,
  });

  return (
    <StyledTabs
      aria-label="Task Page Tabs"
      onValueChange={handleTabChange}
      value={tabIndex}
    >
      {activeTabs.map((tab) => tabMap[tab])}
    </StyledTabs>
  );
};

export default TaskTabs;
